import React from "react";
import { PopupButton } from "@typeform/embed-react";
import { graphql, useStaticQuery } from "gatsby";

import "../../scss/main.scss";
import Footer from "../components/Footer";
import Header from "../components/Header";
import IndeedLogo from "../components/Indeed/IndeedLogo";
import IndeedStars from "../components/Indeed/IndeedStars";
import Seo from "../components/Seo";

const imagePath = "/assets/marketing-site/images";

const benefits = [
	{
		number: "01",
		title: "Pay that reflects the work",
		copy:
			"Competitive weekly pay starts from £800 and rises with the needs and complexity of each placement.",
	},
	{
		number: "02",
		title: "Care packages you choose",
		copy:
			"Browse our job board and apply to live-in care packages that fit your skills, availability and the work you enjoy.",
	},
	{
		number: "03",
		title: "Support with you every day",
		copy:
			"You will have 24/7 access to a care management team that knows the person and the placement.",
	},
];

const placementFlow = [
	{
		number: "01",
		label: "Browse",
		title: "See the full picture",
		copy:
			"Explore live care packages with the rate, rota, location and care requirements clearly set out.",
	},
	{
		number: "02",
		label: "Choose",
		title: "Apply to the packages you like",
		copy:
			"Put yourself forward for the placements that feel right for your skills, experience and availability.",
	},
	{
		number: "03",
		label: "Connect",
		title: "Talk it through before you agree",
		copy:
			"Our team will answer questions, check the detail with you and support a considered next step.",
	},
];

const requirements = [
	"At least 12 months of professional care experience",
	"The right to work in the UK",
	"Confident spoken and written English",
	"A smartphone and confidence using digital care tools",
	"The ability to travel to placements across England",
	"A thoughtful, reliable approach to care",
];

const applicationSteps = [
	{
		number: "01",
		title: "Apply to join Edyn",
		copy:
			"Tell us about your professional experience, availability and the kind of care you enjoy providing.",
	},
	{
		number: "02",
		title: "Show us how you think",
		copy:
			"A short scenario and English assessment helps us understand your judgement and communication.",
	},
	{
		number: "03",
		title: "Meet the team and get ready",
		copy:
			"We will invite successful candidates to a one-to-one interview, complete the necessary checks and prepare you for placement.",
	},
	{
		number: "04",
		title: "Choose packages that suit you",
		copy:
			"Once you are approved, browse the job board and apply to the live-in care packages you would like to work on.",
	},
];

const faqs = [
	{
		question: "What does a live-in carer do day to day?",
		answer:
			"Every placement is different. You may support personal care, medication, meals, mobility, appointments, household routines and companionship. Before every placement, you receive the care plan and risk information needed to provide safe, thoughtful support.",
	},
	{
		question: "What are the pay rates?",
		answer:
			"Pay starts from £800 per week and increases according to the needs and complexity of the care package. We will always explain the rate for a placement before you accept it.",
	},
	{
		question: "What rotas are available?",
		answer:
			"Many carers work three weeks on and one week off. Where possible, we also consider alternative rotas such as two weeks on, two weeks off or four weeks on, two weeks off.",
	},
	{
		question: "Where will I work?",
		answer:
			"Edyn placements are across England, so being willing to travel is important. We discuss location, travel and suitability as part of the matching conversation.",
	},
	{
		question: "What training and support will I receive?",
		answer:
			"Every carer completes our induction and required checks before placement. Once you are working, you have access to ongoing learning, regular contact and support from our care management team.",
	},
	{
		question: "Do you offer a food allowance?",
		answer:
			"Where food cannot be suitably matched between carer and client, we provide a GBP 50 weekly food allowance. This covers main food items, while staple items such as bread and milk are supplied in the home.",
	},
	{
		question: "Do you pay for travel?",
		answer:
			"Carers arrange and pay for their own travel to placements. We discuss location and travel expectations during the matching process so you can make an informed decision before accepting a placement.",
	},
	{
		question: "How soon can I start?",
		answer:
			"Before starting, every carer completes induction training, recruitment checks and an enhanced DBS check. Once these are complete, we can begin matching you with suitable placements.",
	},
];

const ApplyButton = ({ className = "" }) => (
	<PopupButton id="oEtQ7cos" className={`career-apply ${className}`.trim()}>
		Apply now
	</PopupButton>
);

export default function BecomeACarer() {
	const heroVideoRef = React.useRef(null);
	const { heroVideo } = useStaticQuery(graphql`
		query BecomeACarerHeroVideo {
			heroVideo: file(relativePath: { eq: "becomeacarervideo.mov" }) {
				childVideoFfmpeg {
					webm: transcode(
						outputOptions: ["-crf 31", "-b:v 0"]
						maxWidth: 900
						maxHeight: 480
						fileExtension: "webm"
						codec: "libvpx-vp9"
					) {
						fileExtension
						src
					}
					mp4: transcode(
						maxWidth: 900
						maxHeight: 480
						fileExtension: "mp4"
						codec: "libx264"
					) {
						fileExtension
						src
					}
				}
			}
		}
	`);
	const heroVideoFile = heroVideo.childVideoFfmpeg;

	React.useEffect(() => {
		const video = heroVideoRef.current;
		if (!video) return undefined;

		const startVideo = () => {
			video.muted = true;
			video.defaultMuted = true;
			video.play().catch(() => {});
		};

		startVideo();
		video.addEventListener("canplay", startVideo, { once: true });

		return () => video.removeEventListener("canplay", startVideo);
	}, []);

	return (
		<div className="container edyn-carer-careers">
			<Seo
				title="Become a Professional Live-in Carer | Edyn Care"
				description="Build a rewarding live-in care career with Edyn, with fair treatment, professional development, quality support and carefully matched placements."
				path="/become-a-carer/"
			/>
			<Header />

			<main id="content-start">
				<section className="career-hero">
					<div className="site-shell career-hero__grid">
						<div className="career-hero__copy">
							<p className="eyebrow eyebrow--light">Live-in care careers</p>
							<h1>Care work that is recognised. A career you can grow.</h1>
							<p className="career-hero__lead">
								Bring your experience, judgement and kindness to a team that
								sees what professional carers make possible every day.
							</p>
							<div className="career-hero__actions">
								<ApplyButton />
								<a className="career-text-link" href="#career-journey">
									How joining works <span aria-hidden="true">&#8595;</span>
								</a>
							</div>
							<p className="career-hero__note">
								For experienced carers ready for a more supported live-in role.
							</p>
						</div>

						<div className="career-hero__visual">
							<div className="career-hero__video" aria-hidden="true">
								<video
									ref={heroVideoRef}
									autoPlay
									loop
									muted
									playsInline
									preload="auto"
								>
									<source
										src={heroVideoFile.webm.src}
										type={`video/${heroVideoFile.webm.fileExtension}`}
									/>
									<source
										src={heroVideoFile.mp4.src}
										type={`video/${heroVideoFile.mp4.fileExtension}`}
									/>
								</video>
							</div>
							<img
								className="career-hero__fallback"
								src={`${imagePath}/becomeacarer-hero.png`}
								alt="A professional carer sharing a warm moment with an older person"
								width="3971"
								height="2383"
							/>
							<div
								className="career-hero__indeed"
								aria-label="Rated excellent on Indeed"
							>
								<IndeedStars className="career-hero__indeed-stars" score={5} />
								<span>Rated excellent on</span>
								<IndeedLogo className="career-hero__indeed-logo" />
							</div>
							<div className="career-hero__pay">
								<span>From</span>
								<strong>£800</strong>
								<p>per week, with pay reflecting the care package.</p>
							</div>
						</div>
					</div>
				</section>

				<section
					className="career-placement-path"
					aria-labelledby="career-placement-path-title"
				>
					<div className="site-shell career-placement-path__panel">
						<div className="career-placement-path__heading">
							<div>
								<p className="eyebrow">Your placement, your choice</p>
								<h2 id="career-placement-path-title">
									Choose care packages that work for you.
								</h2>
							</div>
							<p>
								Once you are part of Edyn, the job board gives you a clear view
								of live opportunities so you can apply to the placements that
								feel right.
							</p>
						</div>
						<ol className="career-placement-path__steps">
							{placementFlow.map((step) => (
								<li key={step.number}>
									<span>{step.number}</span>
									<p>{step.label}</p>
									<h3>{step.title}</h3>
									<p>{step.copy}</p>
								</li>
							))}
						</ol>
					</div>
				</section>

				<section className="career-benefits section-space">
					<div className="site-shell">
						<div className="career-section-heading">
							<p className="eyebrow">Why Edyn</p>
							<h2>The care you give deserves good support behind it.</h2>
							<p>
								Good care takes skill, focus and resilience. We build the
								conditions that help you do it well and keep building your
								career.
							</p>
						</div>
						<div className="career-benefits__grid">
							{benefits.map((benefit) => (
								<article key={benefit.number}>
									<span>{benefit.number}</span>
									<h3>{benefit.title}</h3>
									<p>{benefit.copy}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="career-requirements">
					<div className="site-shell career-requirements__grid">
						<div>
							<p className="eyebrow eyebrow--light">Is this for you?</p>
							<h2>We are looking for capable people who take pride in care.</h2>
							<p>
								Live-in care is personal work. You will be trusted in someone
								else's home, so we look for the practical experience and human
								qualities that make that trust feel well placed.
							</p>
						</div>
						<ul>
							{requirements.map((requirement) => (
								<li key={requirement}>
									<span aria-hidden="true">&#10003;</span>
									{requirement}
								</li>
							))}
						</ul>
					</div>
				</section>

				<section className="career-support section-space">
					<div className="site-shell career-support__grid">
						<div className="career-support__gallery" aria-hidden="true">
							<img
								src={`${imagePath}/wecare-grid-1.png`}
								alt=""
								width="716"
								height="890"
							/>
							<img
								src={`${imagePath}/wecare-grid-3.png`}
								alt=""
								width="716"
								height="890"
							/>
							<p>Care is personal. Support should be too.</p>
						</div>
						<div className="career-support__copy">
							<p className="eyebrow">On placement, never alone</p>
							<h2>Your work is independent. Your support is not.</h2>
							<p>
								When you move into a client's home, a wider Edyn team remains
								close. We coordinate the detail, give you a clear route for
								questions and keep supporting the placement as needs change.
							</p>
							<div className="career-support__points">
								<p>
									<strong>Care management</strong>
									<span>
										Advice and escalation when you need it, day or night.
									</span>
								</p>
								<p>
									<strong>Clear care plans</strong>
									<span>
										Everything you need to understand the person and home.
									</span>
								</p>
								<p>
									<strong>Quality and development</strong>
									<span>
										Regular contact, learning and feedback that helps you
										progress.
									</span>
								</p>
							</div>
						</div>
					</div>
				</section>

				<section id="career-journey" className="career-journey">
					<div className="site-shell">
						<div className="career-journey__heading">
							<div>
								<p className="eyebrow">Your route into Edyn</p>
								<h2>Join Edyn, then choose your placements.</h2>
							</div>
							<p>
								We get to know the person behind the application, prepare you
								well and then give you the confidence to choose the care
								packages that suit you.
							</p>
						</div>
						<ol className="career-journey__steps">
							{applicationSteps.map((step) => (
								<li key={step.number}>
									<span>{step.number}</span>
									<h3>{step.title}</h3>
									<p>{step.copy}</p>
								</li>
							))}
						</ol>
						<div className="career-journey__apply">
							<p>Ready to start your Edyn application?</p>
							<ApplyButton className="career-apply--ink" />
						</div>
					</div>
				</section>

				<section className="career-learning section-space">
					<div className="site-shell career-learning__panel">
						<div className="career-learning__image">
							<img
								src={`${imagePath}/bac-join-2.png`}
								alt="A professional carer completing an online recruitment interview"
								width="1635"
								height="996"
							/>
						</div>
						<div className="career-learning__copy">
							<p className="eyebrow eyebrow--light">Grow with confidence</p>
							<h2>Training that respects what you already know.</h2>
							<p>
								Our induction builds on your professional experience, then gives
								you a practical foundation for working thoughtfully in a
								client's home.
							</p>
							<ul>
								<li>Moving and handling</li>
								<li>Medication</li>
								<li>First aid and safeguarding</li>
								<li>Dementia and complex care</li>
							</ul>
						</div>
					</div>
				</section>

				<section
					className="career-faq section-space"
					aria-labelledby="career-faq-title"
				>
					<div className="site-shell career-faq__grid">
						<div>
							<p className="eyebrow">Questions, answered</p>
							<h2 id="career-faq-title">The practical details matter.</h2>
							<p>
								Here are the answers to questions candidates ask us most often.
							</p>
						</div>
						<div className="career-faq__list">
							{faqs.map((faq) => (
								<details key={faq.question}>
									<summary>{faq.question}</summary>
									<p>{faq.answer}</p>
								</details>
							))}
						</div>
					</div>
				</section>

				<section className="career-final-cta">
					<div className="site-shell career-final-cta__panel">
						<div>
							<p className="eyebrow">Your next placement could be different</p>
							<h2>Bring your care experience somewhere it is valued.</h2>
						</div>
						<div>
							<p>
								Complete a short application to join Edyn. Once you are
								approved, you can explore live care packages and apply for the
								roles that suit you.
							</p>
							<ApplyButton />
						</div>
					</div>
				</section>
			</main>

			<Footer includeCTA={false} />
		</div>
	);
}
